import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { default as appconfig } from "../config.json";

interface AuthProviderProps {
    children: ReactNode;
}

interface AuthContextType {
    token: string;
    authErr: Error | null;
    isSignedIn: boolean;
    appAuthId: string;
    autoAuthorize: boolean;
    authUrlPath: string;
    signOut: () => void;
}

const appAuthId = appconfig.appAuthId;

const AuthContext = createContext<AuthContextType>({
    token: "",
    authErr: null,
    isSignedIn: false,
    appAuthId: appconfig.appAuthId,
    autoAuthorize: false,
    authUrlPath: "",
    signOut: () => {}
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [token, setToken] = useState<string>(localStorage.getItem("authToken") || "");
    const [authErr, setAuthErr] = useState<Error | null>(null);
    const [isSignedIn, setIsSignedIn] = useState<boolean>(!!localStorage.getItem("authToken"));
    const [autoAuthorize, setAutoAuthorize] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [authUrlPath, setAuthUrlPath] = useState<string>("");

    useEffect(() => {
        if (window.location.hash) {
            const hash = new URLSearchParams((window.location.hash ?? "#").substring(1));
            if (hash.has("access_token")) {
                const newToken = (hash.get("access_token") ?? "").toString();
                setToken(newToken);
                localStorage.setItem("authToken", newToken); // Storing token in local storage to allow it to persist across browser tabs
                setAuthUrlPath(window.location.hash);
                const isValidToken = newToken !== undefined && newToken !== "";
                setIsSignedIn(isValidToken);
            }
            if (hash.has("error")) {
                const key: string = (hash.get("error") ?? "").toString(),
                    desc: string = (hash.get("error_description") ?? "").toString();
                setAuthErr(
                    new Error(
                        key !== "access_denied" && desc !== "The user denied your request."
                            ? desc
                            : "UserCancel"
                    )
                );
            }
        }
        setIsInitialized(true);
    }, []);

    useEffect(() => {
        if (token) {
            console.info("Token updated");
            setAutoAuthorize(authErr === undefined || authErr === null);
        }
    }, [token, authErr]);

    useEffect(() => {
        const interval = setInterval(() => {
            setToken("");
            localStorage.removeItem("authToken");
            setAuthUrlPath("");
            setIsSignedIn(false);
            setAutoAuthorize(true); //only auto-sign in if the user was timed out
        }, 7200000); //Auto-sign out after token lifespan (2hrs)

        return () => clearInterval(interval);
    }, [token]);

    const signOut = () => {
        setToken("");
        localStorage.removeItem("authToken");
        setAuthUrlPath("");
        setIsSignedIn(false);
        setAutoAuthorize(false);
    };

    if (!isInitialized) {
        return null; // Loading...
    }

    return (
        <AuthContext.Provider
            value={{
                token,
                authErr,
                isSignedIn,
                appAuthId,
                autoAuthorize,
                authUrlPath,
                signOut
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
